/**
 * ConditionChipSlot — where a WHERE / HAVING condition's "+ or" / "+ and" chip
 * sits in the Inline pill view.
 *
 * It is a small badge on the condition's top right corner, shown while that
 * condition is hovered or has focus — so it is still reachable with Tab — and
 * while its picker is open; touch screens, which cannot hover, always show it.
 * The badge floats, so revealing it never moves the row, and a WHERE of any
 * length carries no chips at rest. Once a column is picked the draft editor
 * replaces the chip, and it renders back in the row, fully visible, until it is
 * applied or cancelled.
 *
 * The hover / focus target is the slot's DIRECT parent — the condition's
 * `<li>` for a leaf, the group box for a group's end chip — never a further
 * ancestor, so hovering an outer group box does not reveal the end chip of a
 * group nested inside it. The reverse holds too: while the pointer or focus is
 * inside a nested group box (`data-chip-owner`), the enclosing box's badge
 * stays hidden, so only the innermost group's badge shows and the two corner
 * badges never overlap. The parent must be `position: relative`.
 */

import { useState, type ReactElement, type ReactNode } from 'react';

const REVEAL_ON_HOVER =
  'absolute -top-3.5 right-0 z-10 opacity-0 pointer-events-none transition-opacity duration-100 motion-reduce:transition-none ' +
  '[:hover:not(:has([data-chip-owner]:hover))>&]:opacity-100 ' +
  '[:hover:not(:has([data-chip-owner]:hover))>&]:pointer-events-auto ' +
  '[:focus-within:not(:has([data-chip-owner]:focus-within))>&]:opacity-100 ' +
  '[:focus-within:not(:has([data-chip-owner]:focus-within))>&]:pointer-events-auto ' +
  'has-[[data-state=open]]:opacity-100 has-[[data-state=open]]:pointer-events-auto ' +
  '[@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto';

export function ConditionChipSlot({
  children,
}: {
  readonly children: (onDraftOpenChange: (open: boolean) => void) => ReactNode;
}): ReactElement {
  const [draftOpen, setDraftOpen] = useState(false);
  const floating = !draftOpen;
  return (
    <span className={floating ? REVEAL_ON_HOVER : 'inline-flex'} data-chip-reveal={floating ? 'hover' : 'inline'}>
      {children(setDraftOpen)}
    </span>
  );
}
